import {createElement} from 'react';

import classNames from 'classnames';

import {TypographyClasses} from './constants';

import type {ComponentPropsWithoutRef, ElementType, ReactElement} from 'react';


export type TypographyProps<T extends ElementType = 'div'> = {
    /**
     * HTML element tag to render. Accepts any valid HTML element type string
     * (e.g. `"p"`, `"span"`, `"h2"`, `"div"`). Defaults to `"div"`. This lets
     * you use the correct semantic HTML element while retaining the design-system
     * typography styles — e.g. `as="h1"` with `variant="heading-level-1"`.
     */
    as?: T,

    /**
     * Typography style variant from the design system. Controls `font-size`,
     * `line-height`, and letter-spacing via a global CSS class.
     *
     * Available variants:
     * - Headings: `"heading-level-1"` … `"heading-level-6"`
     * - Body: `"body-level-1"` … `"body-level-4"`
     * - Caption: `"caption-level-1"`, `"caption-level-2"`
     *
     * Defaults to `"body-level-3"`.
     */
    variant?: keyof typeof TypographyClasses,

    /**
     * Font weight modifier applied as a utility class. Accepts `"normal"`,
     * `"semi-bold"`, or `"bold"`. When omitted, the weight is inherited from the
     * variant's base styles.
     */
    fontWeight?: 'normal' | 'semi-bold' | 'bold',
} & ComponentPropsWithoutRef<T>;

/**
 * Polymorphic text component that applies design-system typography styles to
 * any valid HTML element.
 *
 * Choose the `variant` for visual style and `as` for the correct semantic
 * HTML element. For example, use `as="h1"` with `variant="heading-level-1"` to
 * get a properly styled `<h1>` without losing semantic meaning. All native
 * HTML attributes of the target element are forwarded.
 *
 * Use the pre-built shorthand components (`H1`–`H6`) when the element and
 * variant are always the same, to avoid repeating both props.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/core-typography--documentation}
 */
export function Typography<T extends ElementType = 'div'>(props: TypographyProps<T>): ReactElement<unknown> {
    const {
        as: Slug = 'div',
        variant = 'body-level-3',
        fontWeight,
        className,
        children,
        ...otherProps
    } = props;

    return createElement(Slug, {
        ...otherProps,
        className: classNames(
            className,
            fontWeight ? `font-weight-${fontWeight}` : undefined,
            TypographyClasses[variant],
        ),
    }, children);
}
